import type { FundSnapshot, PositionView } from '../../core/types.ts';
import { pct, pctSigned, price, toneClass, usd, usdSigned } from '../lib/format.ts';
import { useFlash } from '../lib/useSnapshot.ts';
import { Empty, Meter, Panel, Pill } from './ui.tsx';
import { sideTheme } from './FundCard.tsx';

function PositionRow({ p, fund }: { p: PositionView; fund: FundSnapshot }) {
  const t = sideTheme(p.side);
  const flash = useFlash(p.unrealizedPnlUsd);

  return (
    <tr className="border-t border-edge/70 align-middle">
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <img src={fund.logo} alt="" className="size-5 rounded-full opacity-80" width={20} height={20} />
          <div className="leading-tight">
            <span className="text-[13px] font-semibold">{p.symbol}</span>
            {/* The arrow needs a gap: flush against the number it reads as a
                leading digit ("↓2.0x" looks like 12.0x). */}
            <span className={`ml-1.5 text-[10px] font-medium ${t.text}`}>
              <span aria-hidden>{t.arrow}</span>
              <span className="sr-only">{p.side}</span> {p.leverage.toFixed(1)}x
            </span>
          </div>
        </div>
      </td>

      <td className="num px-3 py-2.5 text-right text-[13px]">{price(p.avgEntryPrice)}</td>
      <td className="num px-3 py-2.5 text-right text-[13px]">{price(p.markPrice)}</td>
      <td className="num px-3 py-2.5 text-right text-[13px] text-muted">{usd(p.notionalUsd, 0)}</td>

      <td className={`num px-3 py-2.5 text-right text-[13px] font-semibold ${toneClass(p.unrealizedPnlUsd)} ${flash}`}>
        {usdSigned(p.unrealizedPnlUsd)}
        <span className="ml-1 text-[10px] font-normal opacity-70">{pctSigned(p.unrealizedPnlPct, 1)}</span>
      </td>

      <td className="px-3 py-2.5">
        <div className="min-w-[110px]">
          <div className="mb-1 flex items-center justify-between text-[10px] text-dim">
            <span>to target</span>
            <span className="num">{pct(p.progressToTarget, 0)}</span>
          </div>
          <Meter value={p.progressToTarget} tone="up" />
        </div>
      </td>

      <td className="num px-3 py-2.5 text-right text-[12px]">
        <span className="text-up">{price(p.takeProfitPrice)}</span>
      </td>
      <td className="num px-3 py-2.5 text-right text-[12px]">
        <span className={p.trailArmed ? 'text-up' : 'text-down'}>{price(p.stopPrice)}</span>
        {p.trailArmed && (
          <span className="ml-1 text-[9px] text-up" title="Stop has been pulled to breakeven: this trade can no longer lose">
            BE
          </span>
        )}
      </td>

      <td className="px-3 py-2.5 text-center">
        <span className="num text-[11px] text-muted">
          {p.trancheCount}/{p.maxLadderSteps}
        </span>
      </td>

      <td className="px-3 py-2.5">
        <div className="min-w-[90px]">
          <div className="mb-1 flex items-center justify-between text-[10px]">
            <span className="text-dim">liq risk</span>
            <span className={`num ${p.liquidationRisk > 0.5 ? 'text-down' : 'text-dim'}`}>
              {pct(p.liquidationRisk, 0)}
            </span>
          </div>
          <Meter value={p.liquidationRisk} tone="down" />
          <p className="num mt-0.5 text-[9px] text-dim" title="Estimated liquidation price on an isolated-margin basis">
            @ {price(p.liquidationPrice)}
          </p>
        </div>
      </td>
    </tr>
  );
}

export function PositionsTable({ funds }: { funds: FundSnapshot[] }) {
  const rows = funds.flatMap((f) => f.positions.map((p) => ({ p, f })));
  const totalNotional = rows.reduce((s, r) => s + r.p.notionalUsd, 0);
  const totalPnl = rows.reduce((s, r) => s + r.p.unrealizedPnlUsd, 0);

  return (
    <Panel
      title="Open positions"
      subtitle="Stop-loss and take-profit rest as reduce-only triggers on the exchange, so risk is enforced even between ticks."
      bodyClassName=""
      right={
        rows.length > 0 ? (
          <div className="flex items-center gap-2">
            <Pill tone="neutral">{usd(totalNotional, 0)} notional</Pill>
            <Pill tone={totalPnl >= 0 ? 'up' : 'down'}>{usdSigned(totalPnl)}</Pill>
          </div>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <Empty>{funds.some((f) => f.positionSyncPending) ? 'No recorded positions to display.' : 'No open positions. The engine opens a new rung as soon as capital and the risk gate allow.'}</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left">
            <thead>
              <tr className="text-[10px] tracking-wide text-dim uppercase">
                <th className="px-3 py-2 font-medium">Market</th>
                <th className="px-3 py-2 text-right font-medium">Avg entry</th>
                <th className="px-3 py-2 text-right font-medium">Mark</th>
                <th className="px-3 py-2 text-right font-medium">Notional</th>
                <th className="px-3 py-2 text-right font-medium">Open P&L</th>
                <th className="px-3 py-2 font-medium">Progress</th>
                <th className="px-3 py-2 text-right font-medium">Target</th>
                <th className="px-3 py-2 text-right font-medium">Stop</th>
                <th className="px-3 py-2 text-center font-medium">Rungs</th>
                <th className="px-3 py-2 font-medium">Liquidation</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ p, f }) => (
                <PositionRow key={`${f.id}-${p.symbol}`} p={p} fund={f} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
